package ai.jessie.web.settings

import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldOptionalStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.NumInputStyle
import ai.jessie.web.theme.TextAreaStyle
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

internal data class SettingsField(
    val name: String,
    val label: String,
    val value: String,
    val problem: String? = null,
    val readOnly: Boolean = false,
)

@Composable
internal fun settingsField(field: SettingsField, change: (String) -> Unit) {
    val id = "settings-${field.name}"
    fieldBlock(field, id) {
        if (field.name == "address") {
            TextArea(
                value = field.value,
                attrs =
                    TextAreaStyle.toAttrs {
                        id(id)
                        fieldAttributes(field)
                        onInput { change(it.value) }
                    },
            )
        } else {
            val style = if (field.isNumeric()) NumInputStyle else FieldInputStyle
            Input(
                InputType.Text,
                attrs =
                    style.toAttrs {
                        id(id)
                        value(field.value)
                        fieldAttributes(field)
                        onInput { change(it.value) }
                    },
            )
        }
    }
}

@Composable
internal fun settingsSelect(
    field: SettingsField,
    options: Map<String, String>,
    change: (String) -> Unit,
) {
    val id = "settings-${field.name}"
    fieldBlock(field, id) {
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    id(id)
                    fieldAttributes(field)
                    onChange { change(it.value.orEmpty()) }
                }
        ) {
            (mapOf("" to "Bitte wählen") + options).forEach { (value, label) ->
                Option(value, attrs = { if (value == field.value) selected() }) { Text(label) }
            }
        }
    }
}

// The label text is unchanged; a trailing parenthesis such as "(optional)" or the format hint is
// rendered in the quieter suffix style.
@Composable
private fun fieldBlock(field: SettingsField, id: String, control: @Composable () -> Unit) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) {
            val hint = field.label.indexOf(" (")
            if (hint < 0) Text(field.label)
            else {
                Text(field.label.substring(0, hint + 1))
                Span(attrs = FieldOptionalStyle.toAttrs()) { Text(field.label.substring(hint + 1)) }
            }
        }
        control()
        field.problem?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}

// Numbers, the IBAN and VAT rates use the numeric face (`docs/design/README.md`).
private fun SettingsField.isNumeric() = name in numericSettings || name.endsWith(".rate")

private fun AttrsScope<*>.fieldAttributes(field: SettingsField) {
    attr("name", field.name)
    attr("autocomplete", "off")
    if (field.readOnly) attr("readonly", "")
    if (field.problem != null) {
        attr("aria-invalid", "true")
        attr("aria-describedby", "settings-problems")
    }
}
